import React, { useEffect, useState } from 'react';
// Assuming you have installed react-icons: npm install react-icons
import { FaLeaf, FaSeedling, FaHandsHelping, FaAppleAlt } from 'react-icons/fa'; 

// Define the structure for the feature list
interface FeatureProps {
    icon: React.ReactNode;
    title: string;
    description: string;
}

// Data for the feature boxes (Right side content)
const features: FeatureProps[] = [
    {
        icon: <FaLeaf className="text-3xl text-white" />,
        title: "100% Organic",
        description: "100% Organic Vermi Compost for pure, natural soil nourishment.",
    },
    {
        icon: <FaSeedling className="text-3xl text-white" />,
        title: "Pure Natural Compost",
        description: "Unadulterated, Earth-born nourishment for vibrant gardens.",
    },
    {
        icon: <FaHandsHelping className="text-3xl text-white" />,
        title: "Environmentally Safe",
        description: "Environmentally friendly, Earth-Conscious, and Garden-Enhancing.",
    },
    {
        icon: <FaAppleAlt className="text-3xl text-white" />,
        title: "Food Safety Ensured",
        description: "Ensuring food safety, Earth-friendly, and Garden boosting results.",
    },
];

const Benefits: React.FC = () => {
    // Placeholder URLs 
    const IMAGE_BACK = "https://creativefarmer.in/cdn/shop/products/vermi.jpg?v=1616310898"; // Image 1 (Back)
    const IMAGE_FRONT = "https://creativefarmer.in/cdn/shop/products/vermi2_8c02a6ab-84fd-4cd1-a94c-b5d7d417807a.jpg?v=1616320876";   // Image 2 (Front/Overlap)

    const [animatedIndex, setAnimatedIndex] = useState(-1); 

    useEffect(() => {
        // Staggered fade-in animation for feature items
        features.forEach((_, index) => {
            setTimeout(() => {
                setAnimatedIndex(index);
            }, 250 * (index + 1)); 
        });
        // Cleanup function not strictly necessary but good practice
        return () => {
            // Clear any pending timeouts if component unmounts
            features.forEach((_, index) => {
                clearTimeout(250 * (index + 1));
            });
        };
    }, []); 

    return (
        // Added overflow-x-hidden to the main container as an extra safeguard
        <div className="py-16 px-4 sm:px-8 bg-gray-50 overflow-x-hidden"> 
            
            {/* Top Heading */}
            <div className="text-center mb-12">
                <p className="text-lg text-[#4f6a39] font-semibold uppercase">Get Organic Fresh Vegetables with our Vermi Compost</p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2">WHY PEOPLE CHOOSE US</h2>
            </div>
            
            {/* Two-Column Layout Container */}
            <div className="flex flex-col lg:flex-row items-center justify-center max-w-6xl mx-auto">
                
                {/* 1. Left Content Area (Overlapping Images) - Responsive positioning maintained */}
                <div 
                    className="w-full lg:w-1/2 flex justify-center p-4 relative min-h-96 lg:min-h-[500px] order-2 lg:order-1"
                > 
                    
                    {/* Image 1: Back */}
                    <img 
                        src={IMAGE_BACK} 
                        alt="Hands planting a seedling in the soil"
                        className="max-w-xs h-64 md:max-w-md md:h-96 object-cover rounded-lg shadow-xl" 
                        style={{
                            // Adjusted position for mobile centering and desktop offset
                            transform: 'translateX(-10%)',
                        }}
                    />

                    {/* Image 2: Front (Overlapping) */}
                    <img 
                        src={IMAGE_FRONT} 
                        alt="Hands holding dark vermicompost"
                        className="absolute w-52 h-52 sm:w-64 sm:h-64 lg:w-72 lg:h-72 object-cover rounded-lg shadow-2xl border-4 border-white"
                        style={{
                            // Adjusted positioning for better mobile visibility
                            bottom: '10%', // Moved slightly up
                            right: '5%', // Moved closer to the edge
                            transform: 'translateY(10%)', // Shifted slightly up
                            zIndex: 10,
                        }}
                    />
                </div>

                {/* 2. Right Content Area (Features/Bullet Points with Animation) */}
                <div className="w-full lg:w-1/2 p-4 mt-8 lg:mt-0 space-y-10 order-1 lg:order-2">
                    {features.map((feature, index) => (
                        <div 
                            key={index} 
                            // FIX: Removed translate-x classes, now only fades in.
                            className={`flex items-start space-x-4 transition-opacity duration-700 ease-out 
                                        ${animatedIndex >= index ? 'opacity-100' : 'opacity-0'}`}
                        >
                            {/* Icon Circle */}
                            <div className="flex-shrink-0 bg-[#4f6a39] p-3 rounded-full flex items-center justify-center">
                                {feature.icon}
                            </div>
                            
                            {/* Text Content */}
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">{feature.title}</h3>
                                <p className="text-gray-600 mt-1">{feature.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Benefits;